import React, { useContext, useEffect, useState } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Chip, Divider,
  CircularProgress, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import StarIcon from "@material-ui/icons/Star";
import EventIcon from "@material-ui/icons/Event";
import ReceiptIcon from "@material-ui/icons/Receipt";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PrintIcon from "@material-ui/icons/Print";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";

import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import useInvoices from "../../hooks/useInvoices";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1100,
    margin: "0 auto",
  },
  pageTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  planCard: {
    borderRadius: 16,
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 4px 24px ${theme.palette.primary.main}22`,
    marginBottom: theme.spacing(3),
  },
  planName: {
    fontWeight: 800,
    fontSize: "1.5rem",
  },
  planPrice: {
    fontWeight: 700,
    fontSize: "2.2rem",
    color: theme.palette.primary.main,
  },
  planPeriod: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  statusChip: {
    fontWeight: 700,
    fontSize: "0.8rem",
  },
  dueDateBox: {
    borderRadius: 12,
    padding: theme.spacing(2),
    textAlign: "center",
    minWidth: 120,
  },
  dueDays: {
    fontWeight: 800,
    fontSize: "2rem",
    lineHeight: 1,
  },
  featureChip: {
    margin: theme.spacing(0.5),
    borderRadius: 8,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1.05rem",
    margin: theme.spacing(3, 0, 1.5),
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  invoiceTable: {
    borderRadius: 12,
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: theme.palette.primary.main,
    "& th": {
      color: "#fff",
      fontWeight: 700,
    },
  },
  statusBadge: {
    borderRadius: 6,
    padding: "2px 10px",
    fontWeight: 700,
    fontSize: "0.75rem",
    display: "inline-block",
  },
  planUpgradeCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": {
      boxShadow: `0 4px 20px ${theme.palette.primary.main}33`,
      borderColor: theme.palette.primary.main,
    },
  },
  planUpgradeCardCurrent: {
    borderColor: theme.palette.primary.main,
    background: `${theme.palette.primary.main}08`,
  },
  payBtn: {
    borderRadius: 8,
    fontWeight: 700,
    textTransform: "none",
    fontSize: "0.82rem",
  },
}));

const formatCurrency = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString("pt-BR");
};

const formatDatetime = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleString("pt-BR");
};

const daysDiff = (dateStr) => {
  if (!dateStr) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return Math.round((due - now) / 86400000);
};

const STATUS_INVOICE = {
  open:     { label: "Pendente",   bg: "#fef3c7", color: "#92400e" },
  paid:     { label: "Pago",       bg: "#d1fae5", color: "#065f46" },
  canceled: { label: "Cancelado",  bg: "#fee2e2", color: "#991b1b" },
  expired:  { label: "Vencido",    bg: "#fee2e2", color: "#991b1b" },
};

const FEATURES = [
  { key: "useWhatsapp",     label: "WhatsApp" },
  { key: "useFacebook",     label: "Facebook" },
  { key: "useInstagram",    label: "Instagram" },
  { key: "useCampaigns",    label: "Campanhas" },
  { key: "useSchedules",    label: "Agendamentos" },
  { key: "useInternalChat", label: "Chat Interno" },
  { key: "useExternalApi",  label: "API Externa" },
  { key: "useKanban",       label: "Kanban" },
  { key: "useOpenAi",       label: "IA" },
  { key: "useIntegrations", label: "Integrações" },
];

function openReceiptWindow(inv, companyName, planName) {
  const w = window.open("", "_blank", "width=600,height=700");
  if (!w) return;
  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Comprovante #${inv.id}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 520px; margin: 40px auto; color: #111; }
    h1 { font-size: 1.3rem; margin-bottom: 4px; }
    .subtitle { color: #555; font-size: 0.85rem; margin-bottom: 28px; }
    .badge { display: inline-block; padding: 3px 12px; border-radius: 20px; font-weight: 700; font-size: 0.78rem; background: #d1fae5; color: #065f46; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    td { padding: 9px 6px; border-bottom: 1px solid #eee; font-size: 0.9rem; }
    td:first-child { color: #555; width: 45%; }
    td:last-child { font-weight: 600; }
    .total td { font-size: 1.05rem; font-weight: 700; border-bottom: none; padding-top: 16px; }
    .footer { margin-top: 32px; font-size: 0.75rem; color: #888; text-align: center; border-top: 1px solid #eee; padding-top: 16px; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h1>Comprovante de Pagamento</h1>
  <p class="subtitle">Fatura #${inv.id}</p>
  <span class="badge">✓ Pago</span>
  <table>
    <tr><td>Empresa</td><td>${companyName || "—"}</td></tr>
    <tr><td>Plano</td><td>${planName || inv.detail || "—"}</td></tr>
    <tr><td>Descrição</td><td>${inv.detail || "—"}</td></tr>
    <tr><td>Vencimento</td><td>${formatDate(inv.dueDate)}</td></tr>
    <tr><td>Data de pagamento</td><td>${formatDatetime(inv.updatedAt)}</td></tr>
    <tr class="total"><td>Valor pago</td><td>${formatCurrency(inv.value)}</td></tr>
  </table>
  <div class="footer">
    Documento gerado em ${new Date().toLocaleString("pt-BR")}
  </div>
  <script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;
  w.document.write(html);
  w.document.close();
}

export default function MeuPlano() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getPlanCompany, getPlanList } = usePlans();
  const { list: listInvoices } = useInvoices();
  const history = useHistory();
  const location = useLocation();

  const [companyData, setCompanyData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [invoicePage, setInvoicePage] = useState(0);
  const ROWS_PER_PAGE = 5;

  const loadData = async () => {
    try {
      const [comp, invs, plans] = await Promise.all([
        getPlanCompany({}, user.companyId),
        listInvoices(),
        getPlanList(),
      ]);
      setCompanyData(comp);
      setInvoices(Array.isArray(invs) ? invs : []);
      setAllPlans(Array.isArray(plans) ? plans.filter(p => p.isPublic) : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Detecta retorno do gateway de pagamento
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get("payment");
    if (!payment) return;

    if (payment === "success" || payment === "already_paid") {
      toast.success("Pagamento confirmado! Seu plano foi atualizado.");
      setLoading(true);
      loadData();
    } else if (payment === "pending") {
      toast.info("Pagamento em processamento. Aguarde a confirmação.");
    } else if (payment === "error") {
      toast.error("Ocorreu um erro ao processar o pagamento. Entre em contato com o suporte.");
    }

    // Remove o parâmetro da URL sem recarregar a página
    history.replace("/meu-plano");
  }, [location.search]);

  const handlePay = async (invoice) => {
    if (invoice.linkInvoice) {
      window.open(invoice.linkInvoice, "_blank");
      return;
    }
    try {
      setPayingId(invoice.id);
      const { data } = await api.get(`/invoices/${invoice.id}/checkout`);
      if (data?.link) {
        window.open(data.link, "_blank");
      } else {
        toast.info("Link de pagamento não disponível. Entre em contato com o suporte.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setPayingId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const plan = companyData?.plan;
  const dueDate = companyData?.dueDate;
  const days = daysDiff(dueDate);
  const recurrence = companyData?.recurrence;

  const dueColor = days === null ? "#6b7280"
    : days < 0 ? "#dc2626"
    : days <= 7 ? "#d97706"
    : "#16a34a";

  const statusLabel = days === null ? "Desconhecido"
    : days < 0 ? "Vencido"
    : companyData?.status === false ? "Suspenso"
    : "Ativo";

  const statusColor = statusLabel === "Ativo" ? { bg: "#d1fae5", color: "#065f46" }
    : statusLabel === "Vencido" ? { bg: "#fee2e2", color: "#991b1b" }
    : { bg: "#fef3c7", color: "#92400e" };

  const sortedInvoices = [...invoices].sort(
    (a, b) => new Date(b.dueDate) - new Date(a.dueDate)
  );

  const currentPlanId = companyData?.planId || plan?.id;

  return (
    <Box className={classes.root}>
      {/* Título */}
      <Typography variant="h5" className={classes.pageTitle}>
        Meu Plano
      </Typography>
      <Typography variant="body2" className={classes.pageSubtitle}>
        Gerencie sua assinatura, acompanhe vencimentos e visualize seu histórico de pagamentos.
      </Typography>

      {/* ── Card do Plano Atual ── */}
      {plan ? (
        <Card className={classes.planCard} elevation={0}>
          <CardContent style={{ padding: "28px 32px" }}>
            <Grid container spacing={3} alignItems="flex-start">
              {/* Info principal */}
              <Grid item xs={12} md={7}>
                <Box display="flex" alignItems="center" gap={12} style={{ gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                  <Typography className={classes.planName}>{plan.name}</Typography>
                  <span
                    className={classes.statusBadge}
                    style={{ background: statusColor.bg, color: statusColor.color }}
                  >
                    {statusLabel}
                  </span>
                </Box>

                <Box display="flex" alignItems="baseline" style={{ gap: 6, marginBottom: 16 }}>
                  <Typography className={classes.planPrice}>
                    {formatCurrency(plan.amount)}
                  </Typography>
                  <Typography className={classes.planPeriod}>
                    / {recurrence === "yearly" ? "ano" : "mês"}
                  </Typography>
                </Box>

                <Box display="flex" style={{ gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
                  <Chip label={`${plan.users} usuário${plan.users !== 1 ? "s" : ""}`} size="small" variant="outlined" className={classes.featureChip} />
                  <Chip label={`${plan.connections} canal${plan.connections !== 1 ? "is" : ""}`} size="small" variant="outlined" className={classes.featureChip} />
                  <Chip label={`${plan.queues} departamento${plan.queues !== 1 ? "s" : ""}`} size="small" variant="outlined" className={classes.featureChip} />
                </Box>

                <Box>
                  {FEATURES.map(f =>
                    plan[f.key] ? (
                      <Chip
                        key={f.key}
                        label={f.label}
                        size="small"
                        icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
                        style={{ margin: 3, background: "#ede9fe", color: "#5b21b6", fontSize: "0.75rem" }}
                      />
                    ) : null
                  )}
                </Box>
              </Grid>

              {/* Vencimento */}
              <Grid item xs={12} md={5}>
                <Box
                  className={classes.dueDateBox}
                  style={{ background: `${dueColor}11`, border: `1.5px solid ${dueColor}55` }}
                >
                  <EventIcon style={{ color: dueColor, fontSize: 28, marginBottom: 4 }} />
                  <Typography style={{ color: dueColor, fontWeight: 600, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>
                    {days < 0 ? "Vencido há" : "Vence em"}
                  </Typography>
                  <Typography className={classes.dueDays} style={{ color: dueColor }}>
                    {days === null ? "—" : Math.abs(days)}
                  </Typography>
                  <Typography style={{ color: dueColor, fontWeight: 600, fontSize: "0.8rem" }}>
                    {Math.abs(days) === 1 ? "dia" : "dias"}
                  </Typography>
                  <Divider style={{ margin: "12px 0" }} />
                  <Typography variant="body2" style={{ color: "#374151", fontWeight: 600 }}>
                    Vencimento: {formatDate(dueDate)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Card style={{ borderRadius: 12, marginBottom: 24 }} elevation={1}>
          <CardContent>
            <Typography color="textSecondary">Nenhum plano ativo encontrado.</Typography>
          </CardContent>
        </Card>
      )}

      {/* ── Planos Disponíveis ── */}
      {allPlans.length > 1 && (
        <>
          <Typography className={classes.sectionTitle}>
            <StarIcon style={{ fontSize: 20 }} />
            Planos Disponíveis
          </Typography>

          <Grid container spacing={2} style={{ marginBottom: 32 }}>
            {allPlans.map((p) => {
              const isCurrent = p.id === currentPlanId;
              return (
                <Grid key={p.id} item xs={12} sm={6} md={4}>
                  <Card
                    className={`${classes.planUpgradeCard} ${isCurrent ? classes.planUpgradeCardCurrent : ""}`}
                    elevation={0}
                  >
                    <CardContent style={{ padding: 20 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 8 }}>
                        <Typography style={{ fontWeight: 800, fontSize: "1rem" }}>{p.name}</Typography>
                        {isCurrent && (
                          <Chip label="Atual" size="small" color="primary" style={{ fontWeight: 700, fontSize: "0.7rem" }} />
                        )}
                      </Box>

                      <Typography style={{ fontWeight: 800, fontSize: "1.5rem", color: "#6366f1", marginBottom: 4 }}>
                        {formatCurrency(p.amount)}
                        <span style={{ fontWeight: 400, fontSize: "0.85rem", color: "#6b7280" }}>
                          /{p.recurrence === "yearly" ? "ano" : "mês"}
                        </span>
                      </Typography>

                      <Divider style={{ margin: "10px 0" }} />

                      <Box display="flex" flexDirection="column" style={{ gap: 4, marginBottom: 12 }}>
                        <Typography variant="caption" color="textSecondary">
                          ✓ {p.users} usuário{p.users !== 1 ? "s" : ""} &nbsp;
                          ✓ {p.connections} canal{p.connections !== 1 ? "is" : ""} &nbsp;
                          ✓ {p.queues} depto{p.queues !== 1 ? "s" : ""}
                        </Typography>
                        <Box>
                          {FEATURES.filter(f => p[f.key]).map(f => (
                            <Typography key={f.key} variant="caption" display="block" style={{ color: "#374151", lineHeight: 1.6 }}>
                              <CheckCircleOutlineIcon style={{ fontSize: 12, color: "#16a34a", marginRight: 4, verticalAlign: "middle" }} />
                              {f.label}
                            </Typography>
                          ))}
                          {FEATURES.filter(f => !p[f.key]).map(f => (
                            <Typography key={f.key} variant="caption" display="block" style={{ color: "#9ca3af", lineHeight: 1.6 }}>
                              <HighlightOffIcon style={{ fontSize: 12, color: "#d1d5db", marginRight: 4, verticalAlign: "middle" }} />
                              {f.label}
                            </Typography>
                          ))}
                        </Box>
                      </Box>

                      {!isCurrent && (
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          size="small"
                          className={classes.payBtn}
                          onClick={() => toast.info("Entre em contato com o suporte para solicitar a mudança de plano.")}
                        >
                          Solicitar mudança
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* ── Histórico de Faturas ── */}
      <Typography className={classes.sectionTitle}>
        <ReceiptIcon style={{ fontSize: 20 }} />
        Histórico de Faturas
      </Typography>

      {sortedInvoices.length === 0 ? (
        <Typography color="textSecondary" style={{ marginBottom: 24 }}>
          Nenhuma fatura encontrada.
        </Typography>
      ) : (
        <Paper className={classes.invoiceTable} elevation={1} style={{ marginBottom: 32 }}>
          <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInvoices
                  .slice(invoicePage * ROWS_PER_PAGE, invoicePage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((inv) => {
                    const st = STATUS_INVOICE[inv.status] || { label: inv.status || "—", bg: "#f3f4f6", color: "#374151" };
                    const canPay = inv.status === "open" || inv.status === "expired";
                    const isPaid = inv.status === "paid";
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell>{formatDate(inv.dueDate)}</TableCell>
                        <TableCell style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {inv.detail || "—"}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 700 }}>
                          {formatCurrency(inv.value)}
                        </TableCell>
                        <TableCell align="center">
                          <span className={classes.statusBadge} style={{ background: st.bg, color: st.color }}>
                            {st.label}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          {canPay && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              className={classes.payBtn}
                              disabled={payingId === inv.id}
                              startIcon={payingId === inv.id ? <CircularProgress size={12} /> : <OpenInNewIcon style={{ fontSize: 14 }} />}
                              onClick={() => handlePay(inv)}
                            >
                              Pagar
                            </Button>
                          )}
                          {isPaid && (
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.payBtn}
                              style={{ color: "#065f46", borderColor: "#6ee7b7" }}
                              startIcon={<PrintIcon style={{ fontSize: 14 }} />}
                              onClick={() => openReceiptWindow(inv, companyData?.name, plan?.name)}
                            >
                              Comprovante
                            </Button>
                          )}
                          {!canPay && !isPaid && (
                            <Typography variant="caption" color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedInvoices.length}
            page={invoicePage}
            onPageChange={(_, newPage) => setInvoicePage(newPage)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Paper>
      )}
    </Box>
  );
}
